
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, Heart, Sun, Palmtree, Plane, Home } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";

interface SmartPopularSuggestionsProps {
  onSuggestionClick: (suggestion: string) => void;
}

interface SmartSuggestion {
  text: string;
  icon: string;
  reason: 'popular' | 'seasonal' | 'default';
  score: number;
}

export const SmartPopularSuggestions = ({ onSuggestionClick }: SmartPopularSuggestionsProps) => {
  const isMobile = useIsMobile();
  const [suggestions, setSuggestions] = useState<SmartSuggestion[]>([]);
  const [loading, setLoading] = useState(true);

  const iconMap = {
    'MapPin': MapPin,
    'Heart': Heart,
    'Sun': Sun,
    'Palmtree': Palmtree,
    'Plane': Plane,
    'Home': Home
  };

  useEffect(() => {
    const fetchSmartSuggestions = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-smart-suggestions');
        
        if (error) {
          console.error('Error fetching smart suggestions:', error);
          // Fallback to default suggestions
          setSuggestions([
            { text: "All-inclusive Turkije", icon: "Sun", reason: "default", score: 0 },
            { text: "Italië met kinderen", icon: "Heart", reason: "default", score: 0 },
            { text: "Griekenland eilanden", icon: "Palmtree", reason: "default", score: 0 },
            { text: "Spanje Costa Brava", icon: "MapPin", reason: "default", score: 0 },
            { text: "Frankrijk Provence", icon: "Home", reason: "default", score: 0 },
            { text: "Portugal strand", icon: "Plane", reason: "default", score: 0 },
            { text: "Kroatië kust", icon: "Palmtree", reason: "default", score: 0 }
          ]);
        } else {
          setSuggestions(data.suggestions || []);
          console.log('Smart suggestions loaded:', data.suggestions?.length);
        }
      } catch (error) {
        console.error('Failed to load smart suggestions:', error);
        // Set fallback suggestions
        setSuggestions([
          { text: "All-inclusive Turkije", icon: "Sun", reason: "default", score: 0 },
          { text: "Italië met kinderen", icon: "Heart", reason: "default", score: 0 },
          { text: "Griekenland eilanden", icon: "Palmtree", reason: "default", score: 0 }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchSmartSuggestions();
  }, []);

  const handleSuggestionClick = async (suggestion: SmartSuggestion) => {
    // Track the click for analytics
    try {
      await supabase.from('search_analytics').insert({
        search_query: suggestion.text,
        user_session: Math.random().toString(36).substring(7),
        interaction_type: 'suggestion_click',
        results_count: 0,
        search_criteria: {
          source: 'smart_suggestions',
          reason: suggestion.reason,
          score: suggestion.score
        }
      });
    } catch (error) {
      console.error('Failed to track suggestion click:', error);
    }

    onSuggestionClick(suggestion.text);
  };

  if (loading) {
    return (
      <div className="mt-8">
        <h3 className="text-sm font-medium text-gray-700 mb-4 text-left">Populaire zoekopdrachten laden...</h3>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <h3 className="text-sm font-medium text-gray-700 mb-4 text-left">
        Populaire zoekopdrachten:
      </h3>
      
      <div className={`flex flex-wrap gap-3 justify-center ${
        isMobile ? 'grid grid-cols-2 gap-2' : ''
      }`}>
        {suggestions.map((suggestion, index) => {
          const IconComponent = iconMap[suggestion.icon as keyof typeof iconMap] || Heart;
          const isPopular = suggestion.reason === 'popular';
          
          return (
            <Button
              key={`${suggestion.text}-${index}`}
              variant="outline"
              size={isMobile ? "sm" : "sm"}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`text-orange-600 border-orange-200 hover:bg-orange-50 hover:border-orange-300 transition-all duration-200 shadow-sm hover:shadow-md ${
                isMobile ? 'text-xs px-2 py-1 h-auto min-h-[2rem] w-full' : ''
              } ${
                isPopular ? 'ring-1 ring-orange-200 bg-orange-25' : ''
              }`}
            >
              <IconComponent className={`${isMobile ? 'h-2.5 w-2.5 mr-1' : 'h-3 w-3 mr-1.5'}`} />
              <span className={isMobile ? 'text-xs leading-tight' : ''}>{suggestion.text}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};
